import { formatCurrency } from '../utils/currencyUtils';
import { FiTrendingUp, FiCheckCircle, FiAlertCircle, FiTag } from 'react-icons/fi';

const metrics = (s) => {
  const hasPrevious = (s?.totalPreviousOutstandingAmount || 0) > 0;
  const discountAmount = s?.discountAmount || 0;
  // A granted discount is folded into totalAmountPaid on the backend (it counts
  // as settled the moment it's given), so un-bundle it here — otherwise "Total
  // Paid" repeats the same figure the Discount card already shows.
  const cashPaid = Math.max(0, (s?.totalAmountPaid || 0) - discountAmount);

  return [
    {
      label: 'Expected Income',
      value: formatCurrency(s?.totalAmount || 0),
      icon: <FiTrendingUp className="text-blue-600 text-lg" />,
      bg: 'bg-blue-50',
      border: 'border-blue-100',
      valueColor: 'text-gray-900',
    },
    {
      label: 'Total Paid',
      value: formatCurrency(cashPaid),
      caption: discountAmount > 0 ? 'Cash received, excludes discount' : null,
      icon: <FiCheckCircle className="text-green-600 text-lg" />,
      bg: 'bg-green-50',
      border: 'border-green-100',
      valueColor: 'text-green-700',
    },
    {
      // totalOutstandingAmount is the grand total (current term + any carried-over
      // balance from previous terms) — break it down when there's a carry-over so
      // it's clear the figure isn't just this term's fees.
      label: hasPrevious ? 'Total Outstanding' : 'Outstanding',
      value: formatCurrency(s?.totalOutstandingAmount || 0),
      icon: <FiAlertCircle className="text-red-500 text-lg" />,
      bg: 'bg-red-50',
      border: 'border-red-100',
      valueColor: 'text-red-600',
      breakdown: hasPrevious
        ? [
            { label: 'This Term', value: formatCurrency(s?.totalCurrentOutstandingAmount || 0) },
            { label: 'Carried Over', value: formatCurrency(s?.totalPreviousOutstandingAmount || 0), highlight: true },
          ]
        : null,
    },
    {
      label: 'Discount',
      value: formatCurrency(discountAmount),
      caption: discountAmount > 0 ? 'Waived, not cash collected' : null,
      icon: <FiTag className="text-orange-500 text-lg" />,
      bg: 'bg-orange-50',
      border: 'border-orange-100',
      valueColor: 'text-orange-600',
    },
  ];
};

const PaymentSummary = ({ paymentSummary }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
    {metrics(paymentSummary).map((m) => (
      <div key={m.label} className={`rounded-2xl border ${m.border} ${m.bg} px-4 py-4`}>
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 rounded-lg bg-white/70">{m.icon}</div>
          <p className="text-xs text-gray-500 font-medium">{m.label}</p>
        </div>
        <p className={`text-xl font-bold ${m.valueColor}`}>{m.value}</p>
        {m.caption && <p className="text-[11px] text-gray-400 mt-0.5">{m.caption}</p>}
        {m.breakdown && (
          <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-red-100">
            {m.breakdown.map((b, i) => (
              <div key={b.label} className="flex items-center gap-3">
                {i > 0 && <div className="w-px h-6 bg-red-100 shrink-0" />}
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">{b.label}</p>
                  <p className={`text-xs font-semibold ${b.highlight ? 'text-amber-700' : 'text-gray-700'}`}>{b.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
);

export default PaymentSummary;
